import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  { value: 'renovation_complete', label: 'Rénovation complète' },
  { value: 'renovation_partielle', label: 'Rénovation partielle' },
  { value: 'salle_de_bain', label: 'Salle de bain' },
  { value: 'cuisine', label: 'Cuisine' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'carrelage', label: 'Carrelage' }
];

const PROPERTY_TYPES = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' }
];

const BUDGET_RANGES = [
  { value: 'moins_50k', label: 'Moins de 50 000 CHF' },
  { value: '50k_100k', label: '50 000 - 100 000 CHF' },
  { value: '100k_200k', label: '100 000 - 200 000 CHF' },
  { value: 'plus_200k', label: 'Plus de 200 000 CHF' }
];

const TIMELINES = [
  { value: 'urgent', label: 'Urgent (< 3 mois)' },
  { value: '3_6_mois', label: '3-6 mois' },
  { value: '6_12_mois', label: '6-12 mois' },
  { value: 'flexible', label: 'Flexible' }
];

export default function QuoteFormSection() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({
    // Step 1
    project_type: '',
    property_type: '',
    surface_sqm: '',
    location: '',
    // Step 2
    budget_range: '',
    desired_timeline: '',
    project_description: '',
    // Step 3
    client_name: '',
    client_email: ''
  });

  const progress = (step / 3) * 100;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    const uploaded = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${BACKEND_URL}/api/uploads`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploaded.push(response.data.url);
        toast.success(`${file.name} uploadé`);
      } catch (error) {
        toast.error(`Erreur upload ${file.name}`);
      }
    }

    setUploadedFiles(prev => [...prev, ...uploaded]);
    setLoading(false);
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!formData.project_type || !formData.property_type || !formData.location) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return false;
      }
    }
    if (stepNum === 2) {
      if (!formData.project_description) {
        toast.error('Veuillez décrire votre projet');
        return false;
      }
    }
    if (stepNum === 3) {
      if (!formData.client_name || !formData.client_email) {
        toast.error('Veuillez remplir vos coordonnées');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.client_email)) {
        toast.error('Email invalide');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        surface_sqm: formData.surface_sqm ? parseFloat(formData.surface_sqm) : null,
        attached_images: uploadedFiles
      };

      await axios.post(`${BACKEND_URL}/api/quote-requests`, payload);
      
      toast.success('Demande de devis envoyée avec succès !', {
        description: 'Nous reviendrons vers vous rapidement avec un plan clair.'
      });

      // Reset
      setFormData({
        project_type: '',
        property_type: '',
        surface_sqm: '',
        location: '',
        budget_range: '',
        desired_timeline: '',
        project_description: '',
        client_name: '',
        client_email: ''
      });
      setUploadedFiles([]);
      setStep(1);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Card className="rounded-2xl border-[color:var(--hr-border)]" data-testid="quote-request-form">
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <div>
              <CardTitle className="font-['Playfair_Display'] text-2xl" style={{ color: 'var(--hr-text)' }}>
                Demande de devis
              </CardTitle>
              <CardDescription className="text-sm mt-1" style={{ color: 'var(--hr-text-muted)' }}>
                Étape {step} sur 3
              </CardDescription>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Step 1: Projet */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="project_type" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Type de projet *
                </Label>
                <Select value={formData.project_type} onValueChange={(v) => handleChange('project_type', v)}>
                  <SelectTrigger id="project_type" className="rounded-xl mt-1.5" data-testid="quote-project-type">
                    <SelectValue placeholder="Sélectionnez le type" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs mt-1.5" style={{ color: 'var(--hr-text-muted)' }}>
                  Vous pouvez répondre approximativement, nous affinerons ensemble.
                </p>
              </div>

              <div>
                <Label htmlFor="property_type" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Type de bien *
                </Label>
                <Select value={formData.property_type} onValueChange={(v) => handleChange('property_type', v)}>
                  <SelectTrigger id="property_type" className="rounded-xl mt-1.5" data-testid="quote-property-type">
                    <SelectValue placeholder="Appartement ou maison" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="surface_sqm" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Surface approximative (m²)
                </Label>
                <Input 
                  id="surface_sqm"
                  type="number" 
                  placeholder="Ex: 80" 
                  value={formData.surface_sqm}
                  onChange={(e) => handleChange('surface_sqm', e.target.value)}
                  className="rounded-xl mt-1.5"
                  data-testid="quote-surface"
                />
              </div>

              <div>
                <Label htmlFor="location" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Localisation *
                </Label>
                <Input 
                  id="location"
                  placeholder="Ex: Genève, Lausanne, Montreux..." 
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="rounded-xl mt-1.5"
                  data-testid="quote-location"
                />
              </div>
            </div>
          )}

          {/* Step 2: Contraintes */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="budget_range" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Budget estimé
                </Label>
                <Select value={formData.budget_range} onValueChange={(v) => handleChange('budget_range', v)}>
                  <SelectTrigger id="budget_range" className="rounded-xl mt-1.5" data-testid="quote-budget">
                    <SelectValue placeholder="Sélectionnez une fourchette" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {BUDGET_RANGES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="desired_timeline" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Délai souhaité
                </Label>
                <Select value={formData.desired_timeline} onValueChange={(v) => handleChange('desired_timeline', v)}>
                  <SelectTrigger id="desired_timeline" className="rounded-xl mt-1.5" data-testid="quote-timeline">
                    <SelectValue placeholder="Quand souhaitez-vous démarrer ?" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {TIMELINES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="project_description" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Décrivez votre projet *
                </Label>
                <Textarea 
                  id="project_description"
                  placeholder="Décrivez ce que vous souhaitez rénover, vos besoins, vos attentes..."
                  value={formData.project_description}
                  onChange={(e) => handleChange('project_description', e.target.value)}
                  className="rounded-xl mt-1.5 min-h-[120px]"
                  data-testid="quote-description"
                />
                <p className="text-xs mt-1.5" style={{ color: 'var(--hr-text-muted)' }}>
                  Plus vous êtes précis, mieux nous pourrons vous accompagner.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Coordonnées */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <Label htmlFor="client_name" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Votre nom *
                </Label>
                <Input 
                  id="client_name"
                  placeholder="Prénom Nom"
                  value={formData.client_name}
                  onChange={(e) => handleChange('client_name', e.target.value)}
                  className="rounded-xl mt-1.5"
                  data-testid="quote-client-name"
                />
              </div>

              <div>
                <Label htmlFor="client_email" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Votre email *
                </Label>
                <Input 
                  id="client_email"
                  type="email"
                  placeholder="exemple@email.com"
                  value={formData.client_email}
                  onChange={(e) => handleChange('client_email', e.target.value)}
                  className="rounded-xl mt-1.5"
                  data-testid="quote-client-email"
                />
                <p className="text-xs mt-1.5" style={{ color: 'var(--hr-text-muted)' }}>
                  Nous reviendrons vers vous avec un plan clair et structuré.
                </p>
              </div>

              <div>
                <Label htmlFor="file_upload" className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>
                  Photos du projet (optionnel)
                </Label>
                <Input 
                  id="file_upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="rounded-xl mt-1.5"
                  data-testid="quote-file-upload"
                />
                <p className="text-xs mt-1.5" style={{ color: 'var(--hr-text-muted)' }}>
                  {uploadedFiles.length > 0 ? `${uploadedFiles.length} fichier(s) uploadé(s)` : 'Ajoutez des photos pour une estimation plus précise'}
                </p>
              </div>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex items-center justify-between pt-4 border-t" style={{ borderColor: 'var(--hr-border)' }}>
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="rounded-xl"
                data-testid="quote-form-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            ) : <div />}

            {step < 3 ? (
              <Button 
                onClick={handleNext}
                className="rounded-xl ml-auto"
                style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                data-testid="quote-form-next-button"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="rounded-xl ml-auto"
                style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                data-testid="quote-form-submit-button"
              >
                {loading ? 'Envoi...' : 'Recevoir mon devis cadré'}
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}